<template>
	<section>
		<div v-if="voteData" class="content-block display-flex-md align-items-center-md">
			<p class="font-weight-bold heading-text-color mr-100-md">Was this helpful?</p>
			<div class="mt-50-smo btn-group-article">
				<BaseButton
					data-test="upvote-button"
					:class="['button button-outline button-mini article-vote-up', { active: isUpvoted }]"
					@click="placeVote(1)"
				>
					<BaseSvgIcon name="checkmark" height="12" width="12" />
					<span>Yes</span>
				</BaseButton>
				<BaseButton
					data-test="downvote-button"
					:class="['button button-outline button-mini article-vote-down ml-50', { active: isDownvoted }]"
					@click="placeVote(-1)"
				>
					<BaseSvgIcon name="close" height="12" width="12" />
					<span>No</span>
				</BaseButton>
			</div>
			<div class="mt-50-smo ml-100-md">
				<span>{{ upvotes }} out of {{ voteCount }} found this helpful</span>
			</div>
		</div>
		<div v-if="displayFeedbackForm" class="mt-150">
			<form id="voteFeedbackForm" @submit.prevent>
				<InputField
					:field="v$.additionalFeedback"
					field-name="Additional feedback"
					data-test="additional-feedback"
				>
					<template #label>
						<div data-test="feedback-label">{{ feedbackLabel }}</div>
					</template>
					<template #input>
						<textarea
							v-model.trim="state.additionalFeedback"
							data-test="additional-feedback-textarea"
							class="input"
							maxlength="1000"
							name="additionalFeedback"
						/>
					</template>
				</InputField>
				<div class="text-align-right mt-150">
					<SubmitButton
						data-test="submit-button"
						class="col-12-smo mb-100-smo"
						:is-working="syncingWithApi"
						:v$="v$"
						@sb-click="submitFeedback"
					>
						Send feedback
					</SubmitButton>
				</div>
			</form>
		</div>
	</section>
</template>

<script setup lang="ts">
import { computed, reactive, Ref, ref } from 'vue';
import { getHelpCenterVote, postHelpCenterVote } from '@api/help-center';
import { maxLength, required } from '@vuelidate/validators';
import { HelpCenterVote } from 'types/help-center';
import InputField from '@components/form/input-field.vue';
import { MicroFeedbackVotingSubmission } from 'types/micro-feedback';
import SubmitButton from '@components/form/submit-button.vue';
import { submitMicroFeedbackVotingResponse } from '@api/micro-feedback';
import { useAppStore } from '@stores/app';
import { useVuelidate } from '@vuelidate/core';

interface Props {
	articleId: string;
	zendeskId: string;
}
const props = defineProps<Props>();

const maxFeedbackLength = 1000;

const rules = {
	additionalFeedback: {
		required,
		maxLength: maxLength(maxFeedbackLength)
	}
};

const state = reactive({
	additionalFeedback: ''
});

const v$ = useVuelidate(rules, state);

const appStore = useAppStore();

const displayFeedbackForm = ref(false);

const syncingWithApi = ref(false);

const upvotes = ref(0);

const voteCount = ref(0);

const voteData: Ref<HelpCenterVote | null> = ref(null);

const userHasNeverPlacedVote = ref(true);

const feedbackLabel = computed((): string => {
	return isUpvoted.value
		? "We're glad to hear that. Anything that could make it even more helpful?"
		: "We're sorry to hear that. What could make it more helpful?";
});

const isUpvoted = computed((): boolean => Number(voteData.value?.value) === 1);

const isDownvoted = computed((): boolean => Number(voteData.value?.value) === -1);

async function placeVote(vote: number): Promise<void> {
	if (voteData.value && vote !== voteData.value.value) {
		if (userHasNeverPlacedVote.value) {
			voteCount.value++;
			if (vote > 0) {
				upvotes.value++;
			}
		} else {
			upvotes.value = Number(upvotes.value) + Number(vote);
		}
		userHasNeverPlacedVote.value = false;

		try {
			voteData.value.value = vote;
			await postHelpCenterVote(voteData.value);
			displayFeedbackForm.value = true;
		} catch {}
	}
}

async function submitFeedback(): Promise<void> {
	syncingWithApi.value = true;
	try {
		const microFeedbackVotingSubmission: MicroFeedbackVotingSubmission = {
			upvoted: isUpvoted.value,
			additionalFeedback: state.additionalFeedback,
			associatedEventId: props.articleId,
			microFeedbackEventType: 'HELP_CENTER_ARTICLE_VOTE'
		};
		await submitMicroFeedbackVotingResponse(microFeedbackVotingSubmission);
	} finally {
		displayFeedbackForm.value = false;
		syncingWithApi.value = false;
		state.additionalFeedback = '';
		v$.value.additionalFeedback.$reset();

		appStore.addToast({
			type: 'success',
			message: 'Thanks for sharing your feedback.'
		});
	}
}

async function setup(): Promise<void> {
	try {
		voteData.value = await getHelpCenterVote(props.articleId);
		upvotes.value = voteData.value.upvotes;
		voteCount.value = voteData.value.vote_count;
	} catch {}
}
setup();
</script>
<style lang="scss" scoped>
.article-vote-up,
.article-vote-down {
	.svg-icon {
		margin-right: 0.5rem;
	}
}

.btn-group-article {
	min-width: 160px;
}

.micro-feedback-textarea {
	height: 150px;
}
</style>
